import {
    Box, Button, TextField, Typography, MenuItem, Checkbox,
    FormControlLabel, Alert, InputAdornment, Paper, LinearProgress
} from "@mui/material";
import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import themeColors from "../theme";
import HeaderBar from "../components/HeaderBar";
import { useAuth } from "../context/AuthContext";
import { formatPhone } from "../utils/validation";
import Autocomplete from "@mui/material/Autocomplete";
import programList from "../shared/programList";
import GradDateField from "../components/GradDateField";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";

const designations = ["MD", "DO", "PA", "NP"];

const statesList = [
    "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
    "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
    "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
    "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
    "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

export default function ProfilePage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { teal, darkTeal, lightGray } = themeColors;

    const [form, setForm] = useState({
        firstName: "", middleName: "", lastName: "",
        designation: "", resident: false, gradDate: "",
        program: "", dob: "", states: [""],
        npi: "", phone: "", npiValidated: false
    });

    const [error, setError] = useState("");
    const [emailStatus, setEmailStatus] = useState(null);
    const [sendingEmail, setSendingEmail] = useState(false);

    const { token, isAuthenticated, refreshUser } = useAuth();
    const showSkip = new URLSearchParams(location.search).get("new") === "true";

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/profile`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (res.data) {
                    setForm({
                        firstName: res.data.firstName || "",
                        middleName: res.data.middleName || "",
                        lastName: res.data.lastName || "",
                        designation: res.data.designation || "",
                        resident: res.data.resident || false,
                        gradDate: res.data.gradDate || "",
                        program: res.data.program || "",
                        dob: res.data.dob ? res.data.dob.slice(0, 10) : "",
                        states: res.data.states?.length ? res.data.states : [""],
                        npi: res.data.npi || "",
                        phone: res.data.phone || "",
                        npiValidated: !!res.data.npiValidated
                    });
                }
            } catch (err) {
                console.error("Error fetching profile", err);
            }
        };

        fetchProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        // For NPI field, only allow digits and limit to 10 characters
        if (name === "npi") {
            const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
            setForm({ ...form, npi: digitsOnly });
            return;
        }
        // Auto-format phone number as XXX-XXX-XXXX
        if (name === "phone") {
            setForm({ ...form, phone: formatPhone(value) });
            return;
        }
        setForm({ ...form, [name]: type === "checkbox" ? checked : value });
    };

    const handleStateChange = (index, value) => {
        const updatedStates = [...form.states];
        updatedStates[index] = value;
        setForm({ ...form, states: updatedStates });
    };

    const addStateField = () => {
        setForm({ ...form, states: [...form.states, ""] });
    };

    const removeState = (index) => {
        const updatedStates = [...form.states];
        updatedStates.splice(index, 1);
        setForm({ ...form, states: updatedStates });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!form.firstName || !form.lastName || !form.designation || !form.dob || !form.states[0]) {
            setError("Please complete all required fields.");
            return;
        }

        if (form.resident && (!form.program || !form.gradDate)) {
            setError("Please provide your program and graduation date.");
            return;
        }

        try {
            await axios.put(`${process.env.REACT_APP_SERVER_URL}/api/profile`, form, {
                headers: { Authorization: `Bearer ${token}` }
            });
            await refreshUser();
            navigate(showSkip ? "/subscribe" : "/dashboard");
        } catch (err) {
            console.error(err);
            setError("Failed to save profile.");
        }
    };

    // Calculate profile completeness
    const calculateCompleteness = () => {
        let filled = 0;
        const total = form.resident ? 8 : 6;

        if (form.firstName) filled++;
        if (form.lastName) filled++;
        if (form.dob) filled++;
        if (form.designation) filled++;
        if (form.states[0]) filled++;
        if (form.npi) filled++;
        if (form.resident) {
            if (form.program) filled++;
            if (form.gradDate) filled++;
        }

        return Math.round((filled / total) * 100);
    };

    const completeness = calculateCompleteness();

    // Test function to send welcome email
    const handleSendTestEmail = async () => {
        setSendingEmail(true);
        setEmailStatus(null);
        try {
            const res = await axios.post(
                `${process.env.REACT_APP_SERVER_URL}/__dev/test-welcome-email`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (res.data.ok) {
                setEmailStatus({ type: "success", message: `Email sent to ${res.data.sentTo}` });
            } else {
                setEmailStatus({ type: "warning", message: res.data.message });
            }
        } catch (err) {
            setEmailStatus({ type: "error", message: err.response?.data?.error || "Failed to send email" });
        } finally {
            setSendingEmail(false);
        }
    };

    return (
        <Box sx={{ minHeight: "100vh", backgroundColor: lightGray }}>
            {isAuthenticated && <HeaderBar limitedNavigation={showSkip} />}

            <Box sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                px: 2,
                py: 6
            }}>
                {/* Header Section */}
                <Box sx={{ textAlign: "center", mb: 4 }}>
                    <Typography variant="h4" fontWeight="600" color="text.primary" gutterBottom>
                        Profile
                    </Typography>

                    {/* Profile Completeness Indicator */}
                    <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                        mt: 2
                    }}>
                        <Typography variant="body2" color="text.secondary">
                            Profile completeness: <strong>{completeness}%</strong>
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={completeness}
                            sx={{
                                width: 200,
                                height: 8,
                                borderRadius: 4,
                                backgroundColor: '#E0E0E0',
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: teal,
                                    borderRadius: 4
                                }
                            }}
                        />
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, maxWidth: 600 }}>
                        Please provide the following information to personalize your experience.
                    </Typography>
                </Box>

                {/* Main Form Container */}
                <Paper
                    elevation={0}
                    sx={{
                        width: "100%",
                        maxWidth: 950,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        overflow: 'hidden'
                    }}
                >
                    <Box component="form" onSubmit={handleSubmit}>
                        {/* Personal Information Section */}
                        <Box sx={{ p: 4, pb: 3 }}>
                            <Typography variant="h6" fontWeight="600" gutterBottom>
                                Personal Information
                            </Typography>

                            {/* Name Fields Row */}
                            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "2fr 1.5fr 2fr" }, gap: 2, mt: 3 }}>
                                <TextField
                                    label="First Name"
                                    name="firstName"
                                    value={form.firstName}
                                    onChange={handleChange}
                                    required
                                    size="small"
                                />
                                <TextField
                                    label="Middle Name"
                                    name="middleName"
                                    value={form.middleName}
                                    onChange={handleChange}
                                    size="small"
                                />
                                <TextField
                                    label="Last Name"
                                    name="lastName"
                                    value={form.lastName}
                                    onChange={handleChange}
                                    required
                                    size="small"
                                />
                            </Box>

                            {/* DOB and Phone Row */}
                            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mt: 2 }}>
                                <TextField
                                    label="DOB"
                                    name="dob"
                                    type="date"
                                    value={form.dob}
                                    onChange={handleChange}
                                    required
                                    size="small"
                                    slotProps={{ inputLabel: { shrink: true } }}
                                />
                                <TextField
                                    label="Phone Number"
                                    name="phone"
                                    value={form.phone}
                                    onChange={handleChange}
                                    placeholder="XXX-XXX-XXXX"
                                    size="small"
                                />
                            </Box>

                            {/* NPI Number */}
                            <Box sx={{ mt: 2 }}>
                                <TextField
                                    fullWidth
                                    label="NPI Number"
                                    name="npi"
                                    value={form.npi}
                                    onChange={handleChange}
                                    required
                                    size="small"
                                    inputProps={{
                                        maxLength: 10,
                                        inputMode: "numeric",
                                        pattern: "[0-9]*"
                                    }}
                                    error={form.npi.length > 0 && form.npi.length !== 10}
                                    helperText={form.npi.length > 0 && form.npi.length !== 10 ? "NPI must be exactly 10 digits" : ""}
                                    slotProps={{
                                        input: {
                                            endAdornment: form.npiValidated ? (
                                                <InputAdornment position="end">
                                                    <CheckCircleIcon sx={{ color: "success.main" }} />
                                                </InputAdornment>
                                            ) : null
                                        }
                                    }}
                                />
                            </Box>
                        </Box>

                        <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />

                        {/* Professional Information Section */}
                        <Box sx={{ p: 4, py: 3 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                                <Typography variant="h6" fontWeight="600">
                                    Professional Information
                                </Typography>

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={form.resident}
                                            onChange={handleChange}
                                            name="resident"
                                            sx={{
                                                color: teal,
                                                '&.Mui-checked': { color: teal }
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography variant="body2">
                                            Are you an EM resident?
                                        </Typography>
                                    }
                                />
                            </Box>

                            {/* Professional Designation and State */}
                            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                                <TextField
                                    select
                                    fullWidth
                                    required
                                    label="Professional Designation"
                                    name="designation"
                                    value={form.designation}
                                    onChange={handleChange}
                                    size="small"
                                >
                                    {designations.map((d) => (
                                        <MenuItem key={d} value={d}>{d}</MenuItem>
                                    ))}
                                </TextField>

                                {/* First State Field (always visible) */}
                                <TextField
                                    select
                                    fullWidth
                                    label="State of Practice"
                                    value={form.states[0]}
                                    onChange={(e) => handleStateChange(0, e.target.value)}
                                    required
                                    size="small"
                                >
                                    {statesList
                                        .filter((s) => s === form.states[0] || !form.states.includes(s))
                                        .map((s) => (
                                            <MenuItem key={s} value={s}>{s}</MenuItem>
                                        ))}
                                </TextField>
                            </Box>

                            {/* Additional States */}
                            {form.states.length > 1 && (
                                <Box sx={{ mt: 2 }}>
                                    {form.states.slice(1).map((state, idx) => {
                                        const actualIdx = idx + 1;
                                        return (
                                            <Box key={actualIdx} sx={{ display: "flex", gap: 1, mb: 1 }}>
                                                <TextField
                                                    select
                                                    fullWidth
                                                    label={`State of Practice ${actualIdx + 1}`}
                                                    value={state}
                                                    onChange={(e) => handleStateChange(actualIdx, e.target.value)}
                                                    size="small"
                                                >
                                                    {statesList
                                                        .filter((s) => s === state || !form.states.includes(s))
                                                        .map((s) => (
                                                            <MenuItem key={s} value={s}>{s}</MenuItem>
                                                        ))}
                                                </TextField>
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    onClick={() => removeState(actualIdx)}
                                                    sx={{ minWidth: "auto", px: 2 }}
                                                    size="small"
                                                >
                                                    Remove
                                                </Button>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}

                            {/* Add Another State Button */}
                            <Button
                                startIcon={<AddIcon />}
                                onClick={addStateField}
                                sx={{
                                    mt: 2,
                                    color: teal,
                                    textTransform: 'none',
                                    fontWeight: 500
                                }}
                                size="small"
                            >
                                Add another state
                            </Button>

                            {/* Resident Program Fields */}
                            {form.resident && (
                                <Box sx={{ mt: 3, pt: 3, borderTop: '1px dashed', borderColor: 'divider' }}>
                                    <Autocomplete
                                        fullWidth
                                        options={programList}
                                        value={form.program}
                                        onChange={(_, newValue) => setForm({ ...form, program: newValue })}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Program"
                                                required
                                                size="small"
                                            />
                                        )}
                                    />

                                    <Box sx={{ mt: 2 }}>
                                        <GradDateField form={form} handleChange={handleChange} />
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        {/* Error Alert */}
                        {error && (
                            <Box sx={{ px: 4, pb: 2 }}>
                                <Alert severity="error" sx={{ borderRadius: 2 }}>
                                    {error}
                                </Alert>
                            </Box>
                        )}

                        {/* Save Button Section */}
                        <Box sx={{
                            p: 4,
                            pt: 3,
                            backgroundColor: '#FAFAFA',
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 2
                        }}>
                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                sx={{
                                    backgroundColor: teal,
                                    '&:hover': { backgroundColor: darkTeal },
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    px: 6,
                                    py: 1.5,
                                    borderRadius: 2,
                                    minWidth: 200
                                }}
                            >
                                SAVE
                            </Button>

                        </Box>
                    </Box>
                </Paper>

                {/* Test Email Button - For Development */}
                {process.env.REACT_APP_SERVER_URL?.includes('localhost') && (
                    <Paper
                        elevation={0}
                        sx={{
                            width: "100%",
                            maxWidth: 950,
                            mt: 3,
                            p: 3,
                            borderRadius: 3,
                            border: '1px dashed',
                            borderColor: 'warning.main',
                            backgroundColor: 'warning.light',
                            opacity: 0.9
                        }}
                    >
                        <Typography variant="subtitle2" color="warning.dark" gutterBottom>
                            Development Testing
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                            <Button
                                variant="outlined"
                                color="warning"
                                onClick={handleSendTestEmail}
                                disabled={sendingEmail}
                                sx={{ textTransform: 'none' }}
                            >
                                {sendingEmail ? 'Sending...' : 'Send Welcome Email'}
                            </Button>
                            {emailStatus && (
                                <Alert severity={emailStatus.type} sx={{ py: 0, flexGrow: 1 }}>
                                    {emailStatus.message}
                                </Alert>
                            )}
                        </Box>
                    </Paper>
                )}
            </Box>
        </Box>
    );
}
